import { useState } from 'react'
import { useSelector } from 'react-redux'
import { DocumentNode } from 'graphql'
import { useMutation } from '@apollo/client'
import { message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { PLACEMENT } from '@Admin/AdminQuotingPage/constants'
import styles from '@Admin/AdminQuotingPage/styles.css'

import { isQueryDetailsQueryRefactorOn } from '~/redux-modules/query-refactor/selector'
import Button from '~/core/Button'
import FileUploader from '~/components/FileUploader'
import logger from '~/lib/logger'

import { createQuoteModelRevisionAttachmentMutation } from './query'
import { useGetOboDetails } from './logic'

type RefetchQueryType = { query: DocumentNode; variables: { id: string } }

type UploadDFMReportButtonPropsType = {
    quoteId: string
    qmrId?: string
    refetchQuery: RefetchQueryType[]
    isManufacturabilityFeedbackTab?: boolean
    isPartViewerModal?: boolean
    quoteUserId?: number
    manualRefetch?: (id: string) => void
}

export default function UploadDFMReportButton(
    props: UploadDFMReportButtonPropsType,
) {
    const {
        quoteId,
        qmrId,
        refetchQuery,
        isManufacturabilityFeedbackTab,
        isPartViewerModal,
        quoteUserId,
        manualRefetch,
    } = props
    const { isObo, oboUserId } = useGetOboDetails(quoteUserId || '')
    const userId = isObo && oboUserId ? oboUserId : null
    const [uploading, setUploading] = useState(false)
    const [createRevisionQuoteAttachment] = useMutation(
        createQuoteModelRevisionAttachmentMutation,
    )

    const isQueryRefactorEnabled = useSelector(isQueryDetailsQueryRefactorOn)

    const onUploadSuccess = async (file: any) => {
        const quoteAttachmentRevisionId = file.quoteAttachmentRevisionId

        setUploading(true)
        try {
            if (isQueryRefactorEnabled && manualRefetch) {
                return await createRevisionQuoteAttachment({
                    variables: {
                        quoteModelRevisionId: qmrId,
                        quoteAttachmentRevisionId: quoteAttachmentRevisionId,
                        userId,
                    },

                    awaitRefetchQueries: true,
                }).then(() => manualRefetch(quoteId))
            } else {
                return await createRevisionQuoteAttachment({
                    variables: {
                        quoteModelRevisionId: qmrId,
                        quoteAttachmentRevisionId: quoteAttachmentRevisionId,
                        userId,
                    },
                    refetchQueries: refetchQuery,
                    awaitRefetchQueries: true,
                })
            }
        } catch (error) {
            logger.exception(error, {
                info: 'AdminQuotingPage:: Failed to createRevisionQuoteAttachment for part DFM report',
                qmrId,
                quoteAttachmentRevisionId,
            })
            message.error(`${file.name} Failed to attach part DFM report.`)
        } finally {
            setUploading(false)
        }
    }

    const onSuccess = () => {
        message.success('Part DFM reports uploaded')
    }

    return (
        <FileUploader
            id={quoteId}
            quoteModelId={qmrId}
            refetchQuery={refetchQuery}
            className={styles.fileUploaderButton}
            onUploadSuccess={onUploadSuccess}
            onSuccess={onSuccess}
            isPartDfmAttachmentType
            buttonSpin
            userId={userId}
            manualRefetch={manualRefetch}
        >
            <Button
                name="upload dfm report"
                type="primary"
                data-test-target={`upload-${PLACEMENT}-section`}
                placement={`upload-${PLACEMENT}-btn`}
                loading={uploading}
                disabled={false}
                style={{
                    width:
                        isManufacturabilityFeedbackTab || isPartViewerModal
                            ? '173px'
                            : '100%',
                }}
                icon={isManufacturabilityFeedbackTab && <UploadOutlined />}
            >
                Upload DFM report
            </Button>
        </FileUploader>
    )
}
